import React, { useState } from 'react';
import { useAnimatedNavigate } from '../../../hooks/useAnimatedNavigate';
import {
  PencilSquareIcon,
  TrashIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { useBillingStore } from '../store/billingStore';
import { useBilling } from '../hooks/useBilling';
import { BILL_STATUS_LABELS } from '../types/billing.types';
import type { Bill, BillStatus } from '../types/billing.types';
import ConfirmModal from './ConfirmModal';

const formatCurrency = (amount: number): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const STATUS_STYLES: Record<BillStatus, string> = {
  draft: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  in_review: 'bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300',
  approved: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300',
  revision: 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300',
  sent: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-300',
  paid: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300',
  overdue: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300',
  cancelled: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-500 line-through',
};

const InvoicesView: React.FC = () => {
  const navigate = useAnimatedNavigate();
  const store = useBillingStore();
  const { transitionBillStatus, deleteBill } = useBilling();
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const handleDelete = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setDeleteConfirm(id);
  };

  const confirmDelete = async () => {
    if (deleteConfirm) {
      await deleteBill(deleteConfirm);
      setDeleteConfirm(null);
    }
  };

  const handleSubmit = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    await transitionBillStatus(id, 'in_review');
  };

  const handleApprove = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    await transitionBillStatus(id, 'approved');
  };

  const handleEdit = (bill: Bill) => {
    store.openDrawer('bill', 'edit', bill.bill_id);
  };

  if (store.bills.length === 0 && !store.loading) {
    return (
      <div className="text-center py-16">
        <p className="text-gray-500 dark:text-gray-400 text-lg">No invoices yet.</p>
        <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">
          Create one from unbilled time entries & expenses.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Table */}
      <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              <th className="px-3 py-3 text-left text-gray-600 dark:text-gray-300 font-medium">Bill #</th>
              <th className="px-3 py-3 text-left text-gray-600 dark:text-gray-300 font-medium">Issue Date</th>
              <th className="px-3 py-3 text-left text-gray-600 dark:text-gray-300 font-medium">Due Date</th>
              <th className="px-3 py-3 text-left text-gray-600 dark:text-gray-300 font-medium">Client</th>
              <th className="px-3 py-3 text-left text-gray-600 dark:text-gray-300 font-medium">Matter</th>
              <th className="px-3 py-3 text-center text-gray-600 dark:text-gray-300 font-medium">Status</th>
              <th className="px-3 py-3 text-right text-gray-600 dark:text-gray-300 font-medium">Subtotal</th>
              <th className="px-3 py-3 text-right text-gray-600 dark:text-gray-300 font-medium">Total</th>
              <th className="px-3 py-3 text-right text-gray-600 dark:text-gray-300 font-medium">Balance</th>
              <th className="px-3 py-3 text-right text-gray-600 dark:text-gray-300 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {store.bills.map((bill, idx) => (
              <tr
                key={bill.bill_id}
                className="lex-list-row hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer"
                style={{ '--row-index': idx } as React.CSSProperties}
                onClick={() => navigate(`/billing/invoices/${bill.bill_id}`)}
              >
                <td className="px-3 py-3 text-gray-900 dark:text-white font-medium whitespace-nowrap">
                  <span style={{ viewTransitionName: `invoice-number-${bill.bill_id}` }}>
                    {bill.bill_number}
                  </span>
                </td>
                <td className="px-3 py-3 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                  {formatDate(bill.issue_date)}
                </td>
                <td className="px-3 py-3 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                  {formatDate(bill.due_date)}
                </td>
                <td className="px-3 py-3 text-gray-700 dark:text-gray-300 whitespace-nowrap max-w-[140px] truncate" title={bill.matter?.client?.client_name || ''}>
                  {bill.matter?.client?.client_name || '-'}
                </td>
                <td className="px-3 py-3 text-gray-700 dark:text-gray-300 whitespace-nowrap max-w-[120px] truncate" title={bill.matter?.matter_number || ''}>
                  {bill.matter?.matter_number || '-'}
                </td>
                <td className="px-3 py-3 text-center">
                  <span className={`inline-flex px-2 py-0.5 text-xs rounded-full ${STATUS_STYLES[bill.status]}`}>
                    {BILL_STATUS_LABELS[bill.status]}
                  </span>
                </td>
                <td className="px-3 py-3 text-right text-gray-700 dark:text-gray-300 tabular-nums whitespace-nowrap">
                  {formatCurrency(Number(bill.subtotal))}
                </td>
                <td className="px-3 py-3 text-right text-gray-900 dark:text-white font-medium tabular-nums whitespace-nowrap">
                  {formatCurrency(Number(bill.total))}
                </td>
                <td className="px-3 py-3 text-right tabular-nums whitespace-nowrap">
                  <span className={Number(bill.balance) > 0 ? 'text-amber-600 dark:text-amber-400 font-medium' : 'text-green-600 dark:text-green-400'}>
                    {formatCurrency(Number(bill.balance))}
                  </span>
                </td>
                <td className="px-3 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-1">
                    {bill.status === 'draft' && (
                      <>
                        <button
                          onClick={() => handleEdit(bill)}
                          className="p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                          title="Edit"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => handleSubmit(e, bill.bill_id)}
                          className="p-1 text-lex-accent hover:bg-lex-accent-soft rounded"
                          title="Submit for Review"
                        >
                          <PaperAirplaneIcon className="h-4 w-4" />
                        </button>
                      </>
                    )}
                    {bill.status === 'in_review' && (
                      <button
                        onClick={(e) => handleApprove(e, bill.bill_id)}
                        className="p-1 text-green-500 hover:bg-green-50 dark:hover:bg-green-900/20 rounded"
                        title="Approve"
                      >
                        <CheckCircleIcon className="h-4 w-4" />
                      </button>
                    )}
                    {bill.status === 'revision' && (
                      <button
                        onClick={() => handleEdit(bill)}
                        className="p-1 text-orange-500 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded"
                        title="Edit (In Revision)"
                      >
                        <PencilSquareIcon className="h-4 w-4" />
                      </button>
                    )}
                    {(bill.status === 'draft' || bill.status === 'cancelled') && (
                      <button
                        onClick={(e) => handleDelete(e, bill.bill_id)}
                        className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                        title="Delete"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination info */}
      <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 px-1">
        <span>
          Showing {store.bills.length} of {store.billPagination.total} invoices
        </span>
        {store.billPagination.has_more && (
          <button
            onClick={() => {
              const newOffset = (store.billFilters.offset || 0) + (store.billFilters.limit || 50);
              store.setBillFilters({ offset: newOffset });
            }}
            className="text-lex-accent hover:underline"
          >
            Load more
          </button>
        )}
      </div>

      <ConfirmModal
        open={deleteConfirm !== null}
        title="Delete Invoice"
        message="This invoice will be permanently deleted. This action cannot be undone."
        confirmLabel="Delete"
        variant="danger"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm(null)}
      />
    </div>
  );
};

export default InvoicesView;
